import { HelpDocumentService } from 'src/app/core/service/help-document.service';
import { HelpDocumentEdit } from '../model/help-document-edit';
import { Component } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';

@Component({
  standalone: false,
  selector: 'app-help-document-list',
  templateUrl: './help-document-list.component.html',
  styleUrls: ['./help-document-list.component.scss']
})
export class HelpDocumentListComponent
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: HelpDocumentEdit;
  public selectedHelpDocument: HelpDocumentEdit;
  public filterText = '';

  /**
   * Constructor
   */
  constructor(
    private helpDocumentService: HelpDocumentService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const helpDocumentId = this.selectedHelpDocument.id;
      this.helpDocumentService.deleteHelpDocument(helpDocumentId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== helpDocumentId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('HELP-DOCUMENT-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedHelpDocument = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.helpDocumentService.getHelpDocumentById(id).subscribe({
      next: (data) =>
      {
        this.selectedHelpDocument = data as HelpDocumentEdit;
        this.open('deleteDialog');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new HelpDocumentEdit();
    });
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.helpDocumentService.getHelpDocumentById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(helpDocumentEdit: HelpDocumentEdit): void
  {
    this.helpDocumentService.saveHelpDocument(helpDocumentEdit, this.isNew, helpDocumentEdit.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedDate into Date object
        data.lastModifiedDate = new Date(data.lastModifiedDate);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('HELP-DOCUMENT-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.helpDocumentService.getHelpDocumentList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedDate = new Date(d.lastModifiedDate); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
